import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { Option, SelectOption } from '@anytag/shared-types';
import { SelectionBar, Button, Select } from '@anytag/shared-atoms';
import { useSelectItems, useToggleState } from '@anytag/shared-hooks';
import {
  TikTokSpecialCampaignPost,
  TikTokSpecialCampaignStatus,
  TikTokSpecialCampaignPostStatus
} from '@anytag/graphql';
import { useUpdateTikTokPostsStatus } from './hooks';

interface ActionBarProps
  extends ReturnType<typeof useSelectItems<TikTokSpecialCampaignPost, keyof TikTokSpecialCampaignPost>> {
  status: TikTokSpecialCampaignStatus;
  campaignId: number;
}

export const ActionBar = ({
  status,
  allItemIds,
  campaignId,
  selectedItems,
  selectedItemIds,
  setSelectedItemIds,
  hasAllItemsSelected,
  toggleSelectAllItems
}: ActionBarProps) => {
  const { t } = useTranslation();
  const loadingState = useToggleState();
  const { isAdminStaff } = useUserRoles();
  const { handleSelectStatus, UpdateTikTokSpecialPostStatusModal } = useUpdateTikTokPostsStatus(campaignId);

  const numberSelectedIds = selectedItemIds.length;
  const options: SelectOption<TikTokSpecialCampaignPostStatus>[] = [
    {
      label: t('Option.Approve post'),
      value: TikTokSpecialCampaignPostStatus.APPROVED,
      hidden: status !== TikTokSpecialCampaignStatus.FINISHED || !isAdminStaff
    },
    {
      label: t('Option.Request to modify'),
      value: TikTokSpecialCampaignPostStatus.WARNING
    },
    {
      label: t('Option.Change to “Effective”'),
      value: TikTokSpecialCampaignPostStatus.WARNING_SOLVED
    },
    {
      hidden: !isAdminStaff,
      label: t('Option.Mark as “Ineffective”'),
      value: TikTokSpecialCampaignPostStatus.INEFFECTIVE
    }
  ].filter((option) => !option.hidden) as Option<TikTokSpecialCampaignPostStatus>[];

  return numberSelectedIds > 0 ? (
    <>
      <SelectionBar
        disabled={loadingState.status}
        selectAllProps={{ onClick: toggleSelectAllItems }}
        checkboxProps={
          allItemIds.length
            ? {
                disabled: loadingState.status,
                onClick: toggleSelectAllItems,
                indeterminate: !hasAllItemsSelected,
                label: t('Selected', { count: numberSelectedIds })
              }
            : undefined
        }
      >
        <Select
          options={options}
          clearable={false}
          className="ml-4 w-45"
          disabled={loadingState.status}
          onChange={(value) => handleSelectStatus(value, selectedItems)}
        />
        <Button
          variant="solid"
          title={t('Button.Save')}
          type="submit"
          css={{ marginLeft: 'auto', padding: '0 16px' }}
        />
      </SelectionBar>

      <UpdateTikTokSpecialPostStatusModal onSuccess={() => setSelectedItemIds([])} />
    </>
  ) : null;
};
